/// <reference path="../../types.d.ts" />
import { state } from "../core/state";
import { t } from "../core/i18n";

export function getParents(pid: string, data = state.data): { father: PersonRecord | null; mother: PersonRecord | null; familyId: string | null } {
  if (!data || !data.individuals) return { father: null, mother: null, familyId: null };
  const p = data.individuals[pid];
  if (!p) return { father: null, mother: null, familyId: null };
  for (const fid of p.famc || []) {
    const fam = data.families[fid];
    if (fam) {
      return {
        father: fam.husb ? data.individuals[fam.husb] || null : null,
        mother: fam.wife ? data.individuals[fam.wife] || null : null,
        familyId: fid,
      };
    }
  }
  return { father: null, mother: null, familyId: null };
}

export interface FamilyUnionDetails {
  familyId: string;
  spouse: PersonRecord | null;
  status: string;
  marriageYear: string;
  marriagePlace: string;
  children: PersonRecord[];
}

export function getSpousesAndChildren(pid: string, data = state.data): FamilyUnionDetails[] {
  if (!data || !data.individuals) return [];
  const p = data.individuals[pid];
  if (!p) return [];
  const unions: FamilyUnionDetails[] = [];
  for (const fid of p.fams || []) {
    const fam = data.families[fid];
    if (!fam) continue;
    const spouseId = fam.husb === pid ? fam.wife : fam.husb;
    const spouse = spouseId ? data.individuals[spouseId] || null : null;
    const children = (fam.children || [])
      .map((cid) => data.individuals[cid])
      .filter((c): c is PersonRecord => Boolean(c));
    unions.push({
      familyId: fid,
      spouse,
      status: fam.status || "current",
      marriageYear: fam.marriageYear || "",
      marriagePlace: fam.marriagePlace || "",
      children,
    });
  }
  return unions;
}

export interface SiblingItem {
  person: PersonRecord;
  isHalf: boolean;
  sharedParent?: "father" | "mother";
  familyId: string;
}

export function getSiblingsDetails(pid: string, data = state.data): SiblingItem[] {
  if (!data || !data.individuals) return [];
  const p = data.individuals[pid];
  if (!p) return [];
  const sibMap = new Map<string, SiblingItem>();

  // Full siblings (same family as child)
  for (const fid of p.famc || []) {
    const fam = data.families[fid];
    if (!fam) continue;
    for (const cid of fam.children || []) {
      const sib = data.individuals[cid];
      if (cid !== pid && sib) {
        sibMap.set(cid, { person: sib, isHalf: false, familyId: fid });
      }
    }
  }

  // Half-siblings via father
  const { father, mother } = getParents(pid, data);
  if (father) {
    for (const fid of father.fams || []) {
      const fam = data.families[fid];
      if (!fam) continue;
      for (const cid of fam.children || []) {
        const sib = data.individuals[cid];
        if (cid !== pid && !sibMap.has(cid) && sib) {
          sibMap.set(cid, { person: sib, isHalf: true, sharedParent: "father", familyId: fid });
        }
      }
    }
  }

  // Half-siblings via mother
  if (mother) {
    for (const fid of mother.fams || []) {
      const fam = data.families[fid];
      if (!fam) continue;
      for (const cid of fam.children || []) {
        const sib = data.individuals[cid];
        if (cid !== pid && !sibMap.has(cid) && sib) {
          sibMap.set(cid, { person: sib, isHalf: true, sharedParent: "mother", familyId: fid });
        }
      }
    }
  }

  return Array.from(sibMap.values());
}

export function getSiblings(pid: string, data = state.data) {
  return getSiblingsDetails(pid, data).map((item) => item.person);
}

export function findShortestKinshipPath(startId: string, targetId: string, data = state.data) {
  if (!data || !data.individuals) return null;
  if (startId === targetId) return [];
  const queue = [[startId]];
  const visited = new Set([startId]);
  const edgeMeta: Record<string, string> = {};

  while (queue.length > 0) {
    const path = queue.shift()!;
    const currId = path[path.length - 1];

    const neighbors: Array<{ id: string; rel: string }> = [];
    const { father, mother } = getParents(currId, data);
    if (father) neighbors.push({ id: father.id, rel: "father" });
    if (mother) neighbors.push({ id: mother.id, rel: "mother" });

    for (const u of getSpousesAndChildren(currId, data)) {
      if (u.spouse) neighbors.push({ id: u.spouse.id, rel: "spouse" });
      for (const ch of u.children) {
        neighbors.push({ id: ch.id, rel: ch.sex === "M" ? "son" : ch.sex === "F" ? "daughter" : "child" });
      }
    }

    for (const sib of getSiblings(currId, data)) {
      neighbors.push({ id: sib.id, rel: sib.sex === "M" ? "brother" : sib.sex === "F" ? "sister" : "sibling" });
    }

    for (const nb of neighbors) {
      if (visited.has(nb.id)) continue;
      visited.add(nb.id);
      edgeMeta[`${currId}->${nb.id}`] = nb.rel;
      const nextPath = [...path, nb.id];
      if (nb.id === targetId) {
        return nextPath.map((pid, idx) => ({
          person: data.individuals[pid],
          relFromPrev: idx === 0 ? null : edgeMeta[`${nextPath[idx - 1]}->${pid}`],
        }));
      }
      queue.push(nextPath);
    }
  }
  return null;
}

export function translateRelStep(rel: string | null) {
  if (!rel) return "";
  const map: Record<string, string> = {
    father: t("stepFather"),
    mother: t("stepMother"),
    parent: t("stepParent"),
    son: t("stepSon"),
    daughter: t("stepDaughter"),
    child: t("stepChild"),
    spouse: t("stepSpouse"),
    brother: t("stepBrother"),
    sister: t("stepSister"),
    sibling: t("stepSibling"),
  };
  return map[rel] || rel;
}

export function getMaternalBranchInfo(pid: string, data = state.data) {
  if (!data || !data.individuals || !data.families) return null;
  const p = data.individuals[pid];
  if (!p) return null;

  const { father, mother, familyId } = getParents(pid, data);
  if (!familyId) return null;

  // Case 1: Father had multiple marriages/spouses
  if (father && father.fams && father.fams.length > 1) {
    const famIndex = Math.max(0, father.fams.indexOf(familyId));
    const motherName = mother ? (mother.given || mother.name.split(" ")[0]) : (state.lang === "tr" ? "Bilinmeyen Anne" : "Unknown Mother");
    const icons = ["🌸", "🌿", "✨", "💜", "🌼", "🍁"];
    const icon = icons[famIndex % icons.length] || "🌸";
    const branchLabel = `${motherName} ${state.lang === "tr" ? "Kolu" : "Branch"}`;
    return {
      type: "mother",
      label: branchLabel,
      icon,
      colorIndex: famIndex % 4,
      familyId,
      parentName: father.name,
      spouseName: mother ? mother.name : motherName,
      fullTitle: `${branchLabel} (${father.name} & ${mother ? mother.name : motherName})`,
    };
  }

  // Case 2: Mother had multiple marriages/spouses (and father didn't)
  if (mother && mother.fams && mother.fams.length > 1) {
    const famIndex = Math.max(0, mother.fams.indexOf(familyId));
    const fatherName = father ? (father.given || father.name.split(" ")[0]) : (state.lang === "tr" ? "Bilinmeyen Baba" : "Unknown Father");
    const icons = ["🌸", "🌿", "✨", "💜", "🌼", "🍁"];
    const icon = icons[famIndex % icons.length] || "🌸";
    const branchLabel = `${fatherName} ${state.lang === "tr" ? "Kolu" : "Branch"}`;
    return {
      type: "father",
      label: branchLabel,
      icon,
      colorIndex: famIndex % 4,
      familyId,
      parentName: mother.name,
      spouseName: father ? father.name : fatherName,
      fullTitle: `${branchLabel} (${mother.name} & ${father ? father.name : fatherName})`,
    };
  }

  return null;
}

export function getBranchPersonIds(rootId: string, data = state.data): Set<string> {
  if (!data || !data.individuals || !data.individuals[rootId]) return new Set();
  const set = new Set<string>([rootId]);
  const q = [rootId];
  while (q.length > 0) {
    const curr = q.shift()!;
    const ind = data.individuals[curr];
    if (!ind) continue;
    for (const famId of (ind.fams || [])) {
      const fam = data.families[famId];
      if (!fam) continue;
      const spouseId = fam.husb === curr ? fam.wife : fam.husb;
      if (spouseId && !set.has(spouseId)) set.add(spouseId);
      for (const chId of (fam.children || [])) {
        if (!set.has(chId)) {
          set.add(chId);
          q.push(chId);
        }
      }
    }
  }
  return set;
}

